/**
 * Permissões no front (W1-58/W1-59) — disable-por-papel.
 *
 * Espelha a matriz RBAC do backend Go (auth/rbac.go) por inteiro — o guardrail
 * permissions.rbac-mirror.test.ts exige igualdade nos dois sentidos. Além de
 * desabilitar ações privilegiadas, decide menu, rotas e cards do Dashboard (#828):
 * permissão ausente aqui esconde o que o backend libera. É o padrão; com
 * sessão carregada, valem as permissões efetivas de GET /bff/auth/me (#897).
 *
 * IMPORTANTE: isto é UX, não segurança. O backend continua a fronteira real —
 * toda escrita passa por RBAC + (quando crítica) MFA no Go, que devolve 403
 * mesmo se a UI deixasse o botão habilitado. Em produção a fonte autoritativa de
 * perfis é o banco (W1-54); o JWT é só uma dica. Por isso esta matriz nunca
 * pode ser MENOR que a do backend: aí a UI esconderia o que a pessoa tem.
 */

const WILDCARD = '*:*'

// Fonte ÚNICA da dica de permissão no front. Nomes canônicos do backend (rbac.go).
// Exportado (só para o guardrail permissions.rbac-mirror.test.ts — não para uso
// fora deste módulo; consuma temPermissao/podeX, não a matriz crua).
export const MATRIZ: Record<string, readonly string[]> = {
  admin: [WILDCARD],
  operador: [
    'cobranca:ver', 'cobranca:criar', // FINC-02
    'rastreador:ver', // RAST-1 (D23)
    'rastreador:criar', // RAST-3 (D23)
    'rastreador:operar', // RAST-4 (D23)
    'rastreador:aprovar', // RAST-5 (D23)
    // #890: o seletor de grau de risco na ficha do bem lê esta lista curada.
    'catalogo_dominio:ver',
    'associado:ver', 'associado:listar', 'associado:criar', 'associado:editar',
    // Achado do guardrail permissions.rbac-mirror.test.ts: faltavam no recorte
    // do front (rbac.go PerfilOperador já concedia as duas).
    'associado:alterar_situacao', 'associado:ver_pii',
    // Reconsulta paga na ficha do associado (crítica + MFA, rbac.go PerfilOperador).
    'associado:reconsultar_pessoa',
    'beneficiario:ver', 'beneficiario:listar', 'beneficiario:criar', 'beneficiario:editar',
    'beneficiario:ver_pii',
    'bem:ver', 'bem:listar', 'bem:criar', 'bem:editar',
    'evento:ver', 'evento:listar', 'evento:criar', 'evento:editar',
    'evento:analisar',
    'financeiro:ver',
    // W1-115: Relatório de Frota (rbac.go PerfilOperador já concedia).
    'relatorio:ver',
    // #584: consulta veicular na ficha do bem.
    'consulta_veicular:consultar',
    // Cotação do bem pela tabela de referência (FIPE).
    'valor_referencia:consultar',
    // Conversa (#199): atende e passa adiante. Não configura setor — isso é
    // organização da operação, não trabalho de quem atende — rbac.go
    // PerfilOperador.
    'atendimento:ver', 'atendimento:criar',
    'atendimento:atribuir',
    // #799: monta o time dos setores que COORDENA. A chave sozinha não dá
    // alcance — o serviço confere a coordenação setor a setor.
    'atendimento:gerenciar_setor',
    // #828: faltavam no front e, com o menu gateado, escondiam o que o backend libera.
    'migracao:ver',
    // COMPRAS/COMP-1: o comprador consulta o catálogo e ABRE solicitação de
    // item novo (D2.1). Não gerencia catálogo nem aprova — aprovar a própria
    // solicitação esbarraria no maker-checker (rbac.go PerfilOperador).
    'compras:ver', 'compras:criar',
  ],
  analista_financeiro: [
    'cobranca:ver', 'cobranca:criar', // FINC-02
    // REDE-04 (W23): lê a rede inteira (só leitura) e o extrato/NF/título dela
    // — rbac.go PerfilAnalistaFinanceiro. `rede:financeiro` não abre o razão.
    'rede:ver', 'rede:financeiro',
    'tabela_preco:ver',
    // CARTAO-01 — cobrança no cartão (rbac.go PerfilAnalistaFinanceiro).
    'cobranca_cartao:ver', 'cobranca_cartao:gerenciar', 'cobranca_cartao:cobrar', 'cobranca_cartao:mandar_boleto',
    'financeiro:ver', 'financeiro:emitir', 'financeiro:baixar', 'financeiro:cancelar',
    'financeiro:conciliar', // FINC-05: decide exceção de pagamento (crítica/MFA)
    'financeiro:editar', // FINC-14: edita/substitui boleto em aberto, avulso e em lote (crítica/MFA)
    'financeiro:exportar', // FINC-11: exporta os relatórios da cobrança (sem MFA)
    'financeiro:configurar', 'financeiro:lancar',
    // WFC-2 (Contas a Pagar): mesma população do razão operacional/boletos
    // ganha aprovação de alçada, pagamento e revelação de PII do fornecedor
    // (rbac.go PerfilAnalistaFinanceiro).
    'financeiro:aprovar', 'financeiro:pagar', 'financeiro:ver_pii',
    // #232: o ato FINANCEIRO da comissão de parceiros — cadastrar dados de
    // pagamento e gerar o título em Contas a Pagar (crítico + MFA). Mesma
    // população que já cadastra fornecedor e registra pagamento no WFC-2
    // (rbac.go PerfilAnalistaFinanceiro).
    'comissao:ver', 'comissao:pagar',
    // COMPRAS/COMP-1: mantém o catálogo de itens e decide a fila de
    // solicitações (a válvula do D2.1) — mesma população que já cadastra
    // fornecedor e responde pelo passivo no WFC-2 (rbac.go
    // PerfilAnalistaFinanceiro).
    // A4 (25/09/2026): `compras:aprovar` SAIU deste perfil — ele cria a
    // solicitação e mantém o catálogo; aprovar é do `gestor` ou do
    // `aprovador_compras`. Ver `compras-segregacao.test.ts`.
    'compras:ver', 'compras:gerenciar', 'compras:criar',
    // #889: quem responde pelo rateio confere a regra que mexe em contribuição
    // mensal e participação. Propõe o gestor, aprova aqui — o analista NÃO
    // configura, senão o maker-checker teria um ator só (rbac.go
    // PerfilAnalistaFinanceiro).
    'desconto_acrescimo:ver', 'desconto_acrescimo:aprovar',
    // Achado do guardrail permissions.rbac-mirror.test.ts: rbac.go
    // PerfilAnalistaFinanceiro já concedia fechar/sacar/resgatar/encerrar do
    // fundo de reserva (W1-18/Fatia 4) — faltavam no recorte do front.
    'rateio:ver', 'rateio:fechar', 'rateio:sacar', 'rateio:resgatar', 'rateio:encerrar',
    // FC-3 Fatia 3/"Parte A" (Contas a Receber): mesma população do rateio
    // ganha leitura de recebíveis/extrato/aging, simulação de re-rateio (não
    // crítica), o gatilho gated de cobrança real e a engine de inadimplência
    // (aplicar re-rateio/excluir/renegociar — críticas/MFA no backend —
    // rbac.go PerfilAnalistaFinanceiro). re_rateio/excluir/renegociar
    // faltavam no recorte do front (achado do guardrail
    // permissions.rbac-mirror.test.ts).
    'receber:ver', 'receber:simular', 'receber:gerar_cobranca',
    'receber:re_rateio', 'receber:excluir', 'receber:renegociar', 'receber:cancelar',
    // Integrações Bancárias: a mesma população que opera financeiro/boletos
    // configura as contas bancárias do tenant (rbac.go PerfilAnalistaFinanceiro).
    // Leitura mascarada (ver/listar) + configurar e testar conexão (críticas/MFA
    // no backend). ver_status = "testar conexão".
    'banco:ver', 'banco:listar', 'banco:configurar', 'banco:ver_status',
    // associado:ver_pii faltava no recorte do front (mesmo achado do guardrail).
    // bem:ver/listar removido: PerfilAnalistaFinanceiro nunca teve permissão
    // de bem no backend — era over-permissive (achado do guardrail).
    'associado:ver', 'associado:listar', 'associado:ver_pii',
    // #828: faltavam no front e, com o menu gateado, escondiam o que o backend libera.
    'beneficiario:ver', 'beneficiario:listar', 'beneficiario:ver_pii',
    'migracao:ver',
    'evento:ver', 'evento:listar',
    // W1-115: Relatório de Frota (rbac.go PerfilAnalistaFinanceiro já concedia).
    'relatorio:ver',
    // Conversa interna (#199): TODO perfil fala com qualquer colega — conversa
    // entre colegas não é privilégio de função. Setor, atribuição e revelação
    // de PII seguem restritos (rbac.go).
    'atendimento:ver', 'atendimento:criar',
  ],
  analista_risco: [
    'associado:ver', 'associado:listar',
    // #828: faltavam no front e, com o menu gateado, escondiam o que o backend libera.
    'beneficiario:ver', 'beneficiario:listar',
    'migracao:ver', 'regras:simular',
    // Achado do guardrail permissions.rbac-mirror.test.ts: rbac.go
    // PerfilAnalistaRisco NUNCA concedeu bem:ver/listar — o front estava
    // over-permissive (botão/dado que sempre 403aria no backend).
    'evento:ver', 'evento:listar', 'evento:criar', 'evento:editar',
    'evento:analisar',
    // W1-115: Relatório de Frota (rbac.go PerfilAnalistaRisco já concedia).
    'relatorio:ver',
    // #584: a ficha do bem passou a disparar consulta veicular persistida, e
    // o botão precisa saber quem pode. rbac.go concede a analista_risco e
    // operador — e a nenhum outro perfil, admin incluído.
    'consulta_veicular:consultar',
    // Cotação do bem pela tabela de referência (FIPE).
    'valor_referencia:consultar',
    // Conversa interna (#199): TODO perfil fala com qualquer colega — conversa
    // entre colegas não é privilégio de função. Setor, atribuição e revelação
    // de PII seguem restritos (rbac.go).
    'atendimento:ver', 'atendimento:criar',
  ],
  gestor: [
    'rastreador:ver', // RAST-1 (D23)
    'rastreador:gerenciar', // RAST-2 (D23)
    'rastreador:criar', // RAST-3 (D23)
    'rastreador:operar', // RAST-4 (D23)
    'rastreador:aprovar', // RAST-5 (D23)
    'rastreador:habilitar', // RAST-6 (D23)
    'tabela_preco:ver', 'tabela_preco:gerenciar',
    // #890: o seletor de grau de risco na ficha do bem lê esta lista curada;
    // a 02b deixa o gestor manter a lista (crítica, MFA).
    'catalogo_dominio:ver', 'catalogo_dominio:gerenciar',
    'rateio:ver', 'rateio:configurar',
    // W1-115: Relatório de Frota (rbac.go PerfilGestor já concedia).
    'relatorio:ver',
    // WFC-2 (Contas a Pagar): gestor é a alçada mais alta hoje na matriz —
    // aprova OC de valor mais alto e o fluxo de exceção NF-avulsa de diretor
    // (rbac.go PerfilGestor).
    'financeiro:ver', 'financeiro:aprovar', 'financeiro:excecao',
    'financeiro:exportar', // FINC-11: exporta os relatórios da cobrança (sem MFA)
    'cobranca:ver', 'cobranca:criar', // FINC-12 (D7): tratativas; a carteira vem do login
    // COMP-7 — define quanto de divergência entre nota e OC passa sem
    // conferência. Fica com quem já decide a EXCEÇÃO, e não com o
    // analista_financeiro, que tem `financeiro:pagar`: quem afrouxa a folga
    // não registra o pagamento. Crítica/MFA no backend.
    'financeiro:tolerancia',
    // Cotação do bem pela tabela de referência (FIPE).
    'valor_referencia:consultar',
    'associado:ver', 'associado:listar', 'associado:incluir_veiculo',
    // #828: faltavam no front e, com o menu gateado, escondiam o que o backend libera.
    'beneficiario:ver', 'beneficiario:listar',
    'migracao:ver', 'regras:simular', 'credito:ver', 'credito:aprovar',
    'evento:ver', 'evento:listar',
    // FC-3 "Parte B": gestor é o "diretor" que autoriza a exceção síncrona
    // do gate suspensão→auxílio (crítica/MFA, rbac.go PerfilGestor).
    'evento:excecao_suspensao',
    // D5 (docs/associados-caminho-certificacao-sga.md, W1-67): gestor vê a
    // saúde do espelho SGA==PPM (dashboard de reconciliação + status de
    // carga) sem poder de disparo — carga:disparar continua só admin.
    'carga:ver_status',
    // PR-CRM-1 (CRM de aquisição): gestor tem a visão TOTAL do funil
    // (crm:gerenciar vê os leads de todos os consultores) além do CRUD
    // (rbac.go PerfilGestor).
    'crm:ver', 'crm:listar', 'crm:criar', 'crm:editar', 'crm:gerenciar',
    // #891: só o gestor mexe em bem que já tem adesão aceita — o boleto já saiu
    // (crítica + MFA, rbac.go PerfilGestor).
    'crm:editar_bem_fechado',
    // #954: gestor reconsulta a placa do bem do lead — gasta crédito da Exato
    // (rbac.go PerfilGestor).
    'crm:reconsultar_placa',
    // #1166: gestor paga de novo a consulta de CPF/CNH já guardada (crítica +
    // MFA, rbac.go PerfilGestor + acoesCriticas).
    'crm:reconsultar_pessoa',
    // Arquivar/desarquivar lead e decidir pedido de arquivamento (rbac.go PerfilGestor).
    'crm:arquivar',
    // Limite de arquivamento do ranking (crítica + MFA, rbac.go PerfilGestor).
    'crm:configurar',
    // §7.1 (PII no lead): APENAS o gestor revela CPF/RG/CNH do lead (crítica +
    // MFA). Consultor/parceiro veem sempre mascarado (rbac.go PerfilGestor).
    // #233: gestor cadastra a meta de contagem do parceiro (rbac.go PerfilGestor).
    'voluntario:gerenciar',
    // #1080: gestor cadastra parceiro à mão (antes do cutover do SGA o catálogo
    // só nascia pelo loader). Ação CRÍTICA + MFA — cria quem recebe comissão e
    // grava CPF de terceiro (rbac.go PerfilGestor + acoesCriticas).
    'voluntario:criar',
    // #232: gestor decide a parte COMERCIAL da comissão — percentual e
    // acompanhamento das metas. NÃO tem comissao:pagar: gerar título em Contas
    // a Pagar e gravar dados bancários do parceiro ficam com quem responde
    // pelo passivo (rbac.go PerfilGestor).
    'comissao:ver', 'comissao:gerenciar',
    // COMPRAS/COMP-1: o gestor decide a fila de solicitações (o sinal 6 do §8
    // é dele), mas não mantém o cadastro — manter catálogo é operação, não
    // gestão (rbac.go PerfilGestor).
    'compras:ver', 'compras:aprovar',
    // #889: gestor configura os limites de desconto e acréscimo da proposta.
    // Sem `aprovar`: a regra que altera contribuição/participação precisa de um
    // segundo ator (rbac.go PerfilGestor).
    'desconto_acrescimo:ver', 'desconto_acrescimo:configurar',
    // CRM Fase 2: gestor administra o catálogo comercial (planos de proteção +
    // adicionais). gerenciar = CRUD (config estrutural, sem MFA) — rbac.go PerfilGestor.
    'catalogo:ver', 'catalogo:gerenciar',
    // CRM Fase 3: gestor administra os formulários/powerlinks de captação. O
    // endpoint público é unauth (fora do RBAC) — rbac.go PerfilGestor.
    'captacao:ver', 'captacao:gerenciar',
    // White-label (PR1): gestor administra o kit de marca do tenant (config, sem
    // MFA). A LEITURA de /branding é universal (ExigirAutenticado, todo logado
    // tema a UI) — não depende de tenant:ver — rbac.go PerfilGestor.
    
    // Integrações de dados (#246): gestor liga/desliga o preenchimento
    // automático por CPF e por placa — rbac.go PerfilGestor.
    'integracao_dados:ver', 'integracao_dados:gerenciar',
    // #1045: gestor grava o token do fornecedor de dados. Ação CRÍTICA + MFA —
    // é o segredo que autoriza gasto de crédito na conta da associação, mesmo
    // patamar de banco:configurar (rbac.go PerfilGestor + acoesCriticas).
    'integracao_dados:credencial',
    // Product analytics (#225): gestor lê o uso agregado do produto (funil do
    // wizard, navegação). A ESCRITA de evento não exige permissão nenhuma —
    // só a leitura, que agrega o comportamento de todos — rbac.go PerfilGestor.
    'analytics:ver',
    // Conversa (#199): gestor atende, atribui e organiza — setor e configuração
    // da integração são decisão de quem coordena a operação — rbac.go
    // PerfilGestor.
    'atendimento:ver', 'atendimento:criar',
    'atendimento:atribuir', 'atendimento:gerenciar', 'atendimento:gerenciar_setor',
    // Revelar PII dentro da conversa (crítica + MFA): só o gestor, mesma
    // decisão do lead no CRM. Operador lê mascarado — rbac.go PerfilGestor.
    // Assinatura eletrônica (PR1, #604): gestor congela contrato + abre
    // sessão de assinatura, e é quem pode REVOGAR uma sessão em andamento
    // (ação crítica + MFA, mesmo verbo/gravidade de financeiro:cancelar) —
    // rbac.go PerfilGestor.
    'assinatura:ver', 'assinatura:criar', 'assinatura:cancelar',
    // Gestor cadastra bem na ficha do associado e acompanha a carteira de
    // veículos: cria, lista e vê (não edita) — rbac.go PerfilGestor.
    'bem:ver', 'bem:listar', 'bem:criar',
  ],
  consultor: [
    // REDE-04 (W23/W27): vê a própria subárvore, cadastra abaixo de si e
    // confirma a exclusão só como gestor direto — rbac.go PerfilConsultor.
    'rede:ver', 'rede:cadastrar', 'rede:excluir',
    // FINC-12 (D7): Central de Boletos, devedores e tratativas, SÓ da carteira dele
    // (aplicado no Go). Sem emitir/baixar/cancelar/exportar/conciliar.
    'financeiro:ver', 'cobranca:ver', 'cobranca:criar',
    'tabela_preco:ver',
    // #890: o seletor de grau de risco na ficha do bem lê esta lista curada.
    'catalogo_dominio:ver',
    'associado:ver', 'associado:listar', 'associado:criar', 'associado:incluir_veiculo',
    'beneficiario:ver', 'beneficiario:listar', 'beneficiario:criar',
    'bem:ver', 'bem:listar',
    // PR B (associado já ativo, 02/09/2026): consultor cadastra um 2º bem pra
    // um associado já ativo, direto na ficha do associado
    // (/associados/{id}/bens → "Adicionar bem") — rbac.go PerfilConsultor.
    'bem:criar',
    // PR-CRM-1 (CRM de aquisição): o consultor opera o próprio funil — vê/edita
    // só os próprios leads (sem crm:gerenciar, que dá a visão de todos —
    // rbac.go PerfilConsultor).
    'crm:ver', 'crm:listar', 'crm:criar', 'crm:editar',
    // Cotação do bem pela tabela de referência (FIPE).
    'valor_referencia:consultar',
    // CRM Fase 2: consultor lê o catálogo p/ ofertar planos na ficha (sem
    // gerenciar) — rbac.go PerfilConsultor.
    'catalogo:ver',
    // CRM Fase 3: consultor vê os formulários de captação (ex.: copiar o próprio
    // powerlink) — sem gerenciar — rbac.go PerfilConsultor.
    'captacao:ver', 'captacao:criar',
    // Assinatura eletrônica (PR1, #604): consultor abre a sessão de
    // assinatura do contrato que ele mesmo vendeu (ver+criar) — sem
    // assinatura:cancelar (revogar é decisão de gestor/admin) — rbac.go
    // PerfilConsultor.
    'assinatura:ver', 'assinatura:criar',
    // Conversa interna (#199): TODO perfil fala com qualquer colega — conversa
    // entre colegas não é privilégio de função. Setor, atribuição e revelação
    // de PII seguem restritos (rbac.go).
    'atendimento:ver', 'atendimento:criar',
  ],
  // #230: voluntário vê só os próprios dados (escopo real resolvido no
  // backend via vinculovoluntario) — rbac.go PerfilVoluntario.
  // #321: parceiro opera o próprio funil de leads no CRM, mesmo escopo do
  // consultor — sem crm:gerenciar (rbac.go PerfilVoluntario).
  // PR3c (#232): parceiro se autocadastra com os PRÓPRIOS dados de pagamento
  // (POST /v1/voluntario/minhas-comissoes/dados-pagamento) — a matriz só
  // concede a AÇÃO, o handler resolve o voluntario_id do PRÓPRIO principal
  // autenticado, nunca um {id} de terceiro (rbac.go PerfilVoluntario).
  // A4 da auditoria da matriz (25/09/2026) — SEGREGAÇÃO DE COMPRAS.
  //
  // Perfil que se SOMA ao de trabalho da pessoa: duas permissões, ver e
  // aprovar, nada de `criar`. O invariante "quem cria não aprova" deixou de
  // depender do maker-checker do banco (que é por usuário) e passou a ser
  // estrutura da matriz — ver `compras-segregacao.test.ts`.
  //
  // A decisão pedida era permissão por USUÁRIO; medido antes de escolher, não
  // existe atribuição por usuário nesta base (só `usuario_perfil` → `perfil`, e
  // `rbac_ajuste` que ajusta por perfil). Este é o fallback decidido pelo dono.
  aprovador_compras: ['compras:ver', 'compras:aprovar'],
  diretor_financeiro: [
    'tabela_preco:ver',
    'tabela_preco:publicar',
    'cobranca:ver',
    'financeiro:ver', // FINC-15 (22/10/2026): lê o financeiro para alcançar a tela do boleto e reabrir baixa
    'financeiro:conciliar',
    'financeiro:reabrir', // FINC-15: desfaz baixa manual (crítica/MFA); quem baixa não é quem desfaz
    'receber:aprovar_acordo', // FINC-17: aprova/rejeita acordo de boletos (crítica/MFA); sempre pessoa diferente de quem propôs
    'financeiro:exportar', // FINC-11: só exporta o que a leitura dele alcança (R4, R5, R6)
    // CARTAO-01 — cobrança no cartão, todas as ações (rbac.go PerfilDiretorFinanceiro).
    'cobranca_cartao:cobrar', 'cobranca_cartao:configurar', 'cobranca_cartao:estornar', 'cobranca_cartao:gerenciar', 'cobranca_cartao:mandar_boleto', 'cobranca_cartao:remover_cartao', 'cobranca_cartao:ver',
  ],

  // ─── Rastreadores (RAST-0M, D23) ────────────────────────────────────────────
  // A concessão chegou por tarefa (RAST-1 … RAST-6, D23); com a RAST-6 a matriz
  // A.2 está completa. `habilitar` é do gestor e do suporte técnico; quem opera
  // o serviço (operador, instalador) não habilita.
  instalador_rastreador: ['rastreador:ver', 'rastreador:criar', 'rastreador:operar', 'rastreador:aprovar'],
  suporte_rastreador: ['rastreador:ver', 'rastreador:habilitar'],

  // ─── Rede Comercial (REDE-04, W15/W23) ─────────────────────────────────────
  //
  // Dica de UI: o escopo de DADO por subárvore é do Go (rede.Escopo), e o MFA
  // (aal2) de `rede:homologar`, `rede:recorrencia` e `rede:desconsiderar_placa` é do backend (acoesCriticas).
  // `admin` herda tudo pelo curinga. Permissões enumeradas, nunca `rede:*`.

  /** Diretoria comercial — tudo da rede, menos o extrato financeiro. */
  diretoria_comercial: [
    'rede:ver', 'rede:cadastrar', 'rede:cadastrar_interno', 'rede:aprovar',
    'rede:excluir', 'rede:recorrencia', 'rede:homologar', 'rede:atribuir_vendedor',
    'rede:permissoes', 'rede:desconsiderar_placa', 'rede:editar_cadastro',
  ],

  /** Gestão de expansão — opera a árvore; sem recorrência, homologação nem financeiro. */
  gestao_expansao: [
    'rede:ver', 'rede:cadastrar', 'rede:cadastrar_interno', 'rede:aprovar',
    'rede:excluir', 'rede:atribuir_vendedor', 'rede:desconsiderar_placa', 'rede:editar_cadastro',
  ],

  /** Vendedor interno — só lê, e só os próprios leads e placas (W23). */
  vendedor_interno: ['rede:ver', 'associado:incluir_veiculo'],

  voluntario: [
    'voluntario:ver', 'voluntario:editar',
    'crm:ver', 'crm:listar', 'crm:criar', 'crm:editar', 'catalogo:ver', 'captacao:ver',
    // Conversa interna (#199): TODO perfil fala com qualquer colega — conversa
    // entre colegas não é privilégio de função. Setor, atribuição e revelação
    // de PII seguem restritos (rbac.go).
    'atendimento:ver', 'atendimento:criar',
  ],

  // ─── Clube de Benefícios (CLUBE-04, spec §8) ───────────────────────────────
  //
  // Staff da OPERADORA (D33): usuário normal do Patrium, mesma autenticação e
  // mesmo MFA — o que muda é só o conjunto de permissões. Prestador, parceiro
  // e membro externo NÃO passam por aqui: autenticam por `clubeacesso` (D32).
  //
  // Nenhum dos três recebe permissão da ASSOCIAÇÃO, e nenhum perfil da
  // associação recebe permissão do clube (D5 — são dois caixas distintos).
  // Quem precisar dos dois mundos recebe os dois perfis; a resolução é união.
  //
  // Nome do PERFIL é `<algo>_clube`; nome do RECURSO é `clube_<algo>`. A
  // inversão é deliberada: assim nenhuma string é ao mesmo tempo perfil e
  // recurso (rbac.go documenta o porquê).
  //
  // CLUBE-C23/D47: `clube_financeiro:resolver_pendencia` (resolver pendência financeira:
  // estornar/reativar/baixar) é a 10ª ação crítica (aal2), concedida a gestor_clube e
  // financeiro_clube.
  // As nove ações críticas — clube_catalogo:publicar, clube_membro:cancelar,
  // clube_membro:ver_pii, clube_financeiro:estornar, clube_financeiro:configurar,
  // clube_repasse:fechar, clube_repasse:pagar, clube_pontos:ajustar e
  // clube_config:editar — aparecem AQUI normalmente: esta matriz é dica de UI,
  // e o gate de MFA (aal2) é do backend, que devolve 403 MFA_REQUERIDA mesmo
  // com o botão habilitado.

  /** Gestor do Clube — os dez recursos da spec §8, todas as ações. */
  gestor_clube: [
    'clube_catalogo:ver', 'clube_catalogo:editar', 'clube_catalogo:publicar',
    'clube_membro:ver', 'clube_membro:ver_pii', 'clube_membro:editar', 'clube_membro:cancelar',
    'clube_membro:convidar',
    'clube_financeiro:ver', 'clube_financeiro:estornar', 'clube_financeiro:configurar',
    'clube_financeiro:resolver_pendencia',
    'clube_chamado:ver', 'clube_chamado:triar', 'clube_chamado:despachar',
    'clube_chamado:concluir_assistido',
    'clube_agenda:ver', 'clube_agenda:editar',
    'clube_repasse:ver', 'clube_repasse:fechar', 'clube_repasse:pagar',
    'clube_parceiro:ver', 'clube_parceiro:editar',
    'clube_pontos:ver', 'clube_pontos:configurar', 'clube_pontos:ajustar',
    'clube_config:ver', 'clube_config:editar',
    'clube_indicador:ver',
  ],

  /**
   * Operação do Clube — chamado, agenda, membro:ver, parceiro:ver.
   * Sem PII do membro, sem fatura, sem repasse, sem catálogo.
   */
  operacao_clube: [
    'clube_chamado:ver', 'clube_chamado:triar', 'clube_chamado:despachar',
    'clube_chamado:concluir_assistido',
    'clube_agenda:ver', 'clube_agenda:editar',
    'clube_membro:ver',
    'clube_parceiro:ver',
  ],

  /** Financeiro do Clube — financeiro, repasse, membro:ver. */
  financeiro_clube: [
    'clube_financeiro:ver', 'clube_financeiro:estornar', 'clube_financeiro:configurar',
    'clube_financeiro:resolver_pendencia',
    'clube_repasse:ver', 'clube_repasse:fechar', 'clube_repasse:pagar',
    'clube_membro:ver',
  ],
}

let permissoesDaSessao: ReadonlySet<string> | null = null
/** Veredito do servidor (GET /bff/auth/me); ver o bloco abaixo. */
export let carteiraRestrita = false

/**
 * Permissões efetivas vindas de GET /bff/auth/me (matriz do código + ajustes do
 * banco, #897). Enquanto não houver sessão carregada, temPermissao usa a MATRIZ.
 */
export function definirPermissoesDaSessao(permissoes: readonly string[] | null, restrita = false): void {
  permissoesDaSessao = permissoes ? new Set(permissoes) : null
  carteiraRestrita = restrita
}

/**
 * FINC-12: o login tem carteira restrita (vínculo de voluntário, cooperativa ou
 * regional)? Vem de `carteira_restrita` em GET /bff/auth/me, calculado no servidor
 * pelo MESMO escopocarteira.Resolvedor que recorta as rotas de leitura — a SPA nunca
 * deduz isto pela lista de perfis. Escopo ilegível chega como restrita (fail-closed).
 * Gravado junto das permissões (`definirPermissoesDaSessao`, 2º argumento).
 */

/**
 * `financeiro:ver` é RECORTADA pela carteira nas rotas por associado (Central de Boletos,
 * devedores, ficha) mas, em todas as outras rotas que a usam como guard (compras, contas a
 * pagar, razão, conciliação, parâmetros, dashboard agregado), exige carteira GLOBAL e
 * responde 404 para quem tem carteira restrita.
 *
 * Por isso `temPermissao` NEGA `financeiro:ver` à carteira restrita (padrão fail-closed:
 * tela nova que gateia por ela já nasce escondida do consultor) e só as poucas telas
 * recortadas pela carteira passam `daCarteira = true`: Central de Boletos, devedores,
 * tratativas e a aba Financeiro da ficha do associado.
 */
/** temPermissao verifica se algum perfil concede a permissão "recurso:acao". */
export function temPermissao(perfis: string[] | undefined, permissao: string, daCarteira?: boolean): boolean {
  if (!daCarteira && carteiraRestrita && permissao === 'financeiro:ver') return false
  if (!perfis || perfis.length === 0) return false
  const [recurso] = permissao.split(':')
  if (permissoesDaSessao) {
    return (
      permissoesDaSessao.has(WILDCARD) ||
      permissoesDaSessao.has(`${recurso}:*`) ||
      permissoesDaSessao.has(permissao)
    )
  }
  return perfis.some((perfil) => {
    const concedidas = MATRIZ[perfil]
    if (!concedidas) return false
    return concedidas.some(
      (p) => p === WILDCARD || p === `${recurso}:*` || p === permissao,
    )
  })
}

export const podeVerMatrizRbac = (perfis?: string[]): boolean => temPermissao(perfis, 'usuario:ver')
export const podeEditarMatrizRbac = (perfis?: string[]): boolean => temPermissao(perfis, 'usuario:gerenciar')

// Financeiro (W1-58)
export const podeEmitirBoleto = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:emitir')
export const podeBaixarBoleto = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:baixar')
export const podeCancelarBoleto = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:cancelar')
export const podeReabrirBoleto = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:reabrir')
export const podeEditarBoleto = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:editar')

// Financeiro Corporativo (WFC-1) — razão operacional
export const podeConfigurarFinanceiro = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:configurar')
export const podeLancarFinanceiro = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:lancar')

// Contas a Receber (FC-3 "Parte A") — leitura, simulação de re-rateio e o
// gatilho gated de cobrança real (crítica/MFA no backend + feature flag
// rateio.cobranca_real.habilitada, também aplicada no backend).
export const podeVerReceber = (perfis?: string[]): boolean => temPermissao(perfis, 'receber:ver')
export const podeSimularReceber = (perfis?: string[]): boolean => temPermissao(perfis, 'receber:simular')
export const podeGerarCobrancaReal = (perfis?: string[]): boolean => temPermissao(perfis, 'receber:gerar_cobranca')
// Engine de inadimplência (FC-3 Fatia 3) — aplicar re-rateio/exclusão/renegociação (críticas/MFA).
export const podeReRateioReceber = (perfis?: string[]): boolean => temPermissao(perfis, 'receber:re_rateio')
export const podeExcluirReceber = (perfis?: string[]): boolean => temPermissao(perfis, 'receber:excluir')
export const podeRenegociarReceber = (perfis?: string[]): boolean => temPermissao(perfis, 'receber:renegociar')
export const podeAprovarAcordoBoleto = (perfis?: string[]): boolean => temPermissao(perfis, 'receber:aprovar_acordo')
export const podeCancelarRecebivel = (perfis?: string[]): boolean => temPermissao(perfis, 'receber:cancelar')

// Contas a Pagar (WFC-2) — fornecedor/OC/título/pagamento/contrato recorrente
export const podeAprovarFinanceiro = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:aprovar')
export const podePagarFinanceiro = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:pagar')
export const podeExcecaoFinanceiro = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:excecao')
export const podeVerPiiFinanceiro = (perfis?: string[]): boolean => temPermissao(perfis, 'financeiro:ver_pii')

// Eventos / Ocorrências (W1-59)
export const podeCriarEvento = (perfis?: string[]): boolean => temPermissao(perfis, 'evento:criar')
export const podeEditarEvento = (perfis?: string[]): boolean => temPermissao(perfis, 'evento:editar')
export const podeAnalisarEvento = (perfis?: string[]): boolean => temPermissao(perfis, 'evento:analisar')
// FC-3 "Parte B": exceção síncrona de gestor ao gate suspensão→auxílio.
export const podeExcecaoSuspensaoEvento = (perfis?: string[]): boolean => temPermissao(perfis, 'evento:excecao_suspensao')

// Beneficiários (W1-60)
export const podeCriarBeneficiario = (perfis?: string[]): boolean => temPermissao(perfis, 'beneficiario:criar')
export const podeEditarBeneficiario = (perfis?: string[]): boolean => temPermissao(perfis, 'beneficiario:editar')

// Conciliação / carga sob demanda (W1-64 Fase B) — hoje só o perfil admin tem
// carga:disparar/carga:ver_status (ver backend/internal/auth/rbac.go), então
// normalmente o botão "Conciliar agora" aparecerá desabilitado para a maioria
// dos perfis — comportamento correto, não um bug.
export const podeExecutarCarga = (perfis?: string[]): boolean => temPermissao(perfis, 'carga:disparar')
export const podeVerStatusCarga = (perfis?: string[]): boolean => temPermissao(perfis, 'carga:ver_status')

// Configurações (PR-C1) — a lista de usuários exige usuario:listar (só o
// perfil admin, via wildcard *:*). A VISIBILIDADE do menu Configurações é um
// OR de permissões-de-configurar declarado no nav-item (requiresAnyPermission),
// não aqui — assim o menu aparece p/ quem pode configurar qualquer recurso.
export const podeListarUsuarios = (perfis?: string[]): boolean => temPermissao(perfis, 'usuario:listar')
// Cockpit da migração (estado-migracao) — a aba Integrações do Configurações
// linka para lá quando o usuário pode ver o estado real da sincronização.
export const podeVerMigracao = (perfis?: string[]): boolean => temPermissao(perfis, 'migracao:ver')

// CRM de aquisição (PR-CRM-1) — funil de vendas. Leitura (ver/listar), escrita
// (criar/editar) e a visão total do funil (gerenciar = ver os leads de todos
// os consultores; sem ela o usuário só enxerga os próprios). O escopo real é
// aplicado no backend (Go); aqui só polimos a UI (nav-item + botões).
export const podeVerCrm = (perfis?: string[]): boolean => temPermissao(perfis, 'crm:ver')
export const podeCriarLead = (perfis?: string[]): boolean => temPermissao(perfis, 'crm:criar')
export const podeEditarLead = (perfis?: string[]): boolean => temPermissao(perfis, 'crm:editar')

/**
 * Mexer em bem que já tem adesão aceita — o boleto de adesão já saiu (#891).
 * Só o gestor, e o backend ainda exige MFA e justificativa escrita: esta
 * checagem existe para OFERECER o caminho a quem pode, nunca para autorizar.
 */
export const podeEditarBemFechado = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'crm:editar_bem_fechado')

export const podeReconsultarPlaca = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'crm:reconsultar_placa')

/** Consultar de novo CPF/CNH já guardados (#1166): cobra outra vez e exige MFA. */
export const podeReconsultarPessoa = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'crm:reconsultar_pessoa')

/** Mesma reconsulta paga, na ficha do associado: ação própria, só operador e admin (crítica + MFA). */
export const podeReconsultarPessoaAssociado = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'associado:reconsultar_pessoa')

/** Arquivar/desarquivar lead e aprovar/recusar pedido. Quem não tem só pede. */
export const podeArquivarLead = (perfis?: string[]): boolean => temPermissao(perfis, 'crm:arquivar')
export const podeGerenciarCrm = (perfis?: string[]): boolean => temPermissao(perfis, 'crm:gerenciar')
/** Editar o limite de arquivamento por consultor (ação crítica, MFA no backend). Quem só tem crm:ver enxerga o valor. */
export const podeConfigurarCrm = (perfis?: string[]): boolean => temPermissao(perfis, 'crm:configurar')
// CRM Fase 2 — catálogo comercial (planos de proteção + adicionais). Leitura
// (ver) para ofertar na ficha; gerenciar (CRUD) para administrar o catálogo.
// Léxico ARGOS: "planos de proteção", nunca "seguro/cobertura". A proposta na
// ficha reusa crm:editar/crm:ver (não tem permissão própria).
export const podeVerCatalogo = (perfis?: string[]): boolean => temPermissao(perfis, 'catalogo:ver')
export const podeGerenciarCatalogo = (perfis?: string[]): boolean => temPermissao(perfis, 'catalogo:gerenciar')
// CRM Fase 3 — captação de leads (formulários públicos + powerlinks). Leitura
// (ver) e gerenciar (CRUD) dos formulários; o endpoint público de submissão é
// unauth (fora do RBAC). A tela admin é a PR2.
export const podeVerCaptacao = (perfis?: string[]): boolean => temPermissao(perfis, 'captacao:ver')
export const podeCriarCaptacao = (perfis?: string[]): boolean => temPermissao(perfis, 'captacao:criar')
export const podeGerenciarCaptacao = (perfis?: string[]): boolean => temPermissao(perfis, 'captacao:gerenciar')
/** #890 02b: criar/renomear/desativar grau de risco e profissão (ação crítica — MFA no backend). */
export const podeGerenciarCatalogoDominio = (perfis?: string[]): boolean => temPermissao(perfis, 'catalogo_dominio:gerenciar')
// Conversão lead→associado (PR-CRM-3): exige as DUAS permissões (o backend gita
// crm:editar na rota + associado:criar no handler). consultor/admin têm ambas;
// o gestor gerencia o funil mas NÃO cria associado → botão desabilitado (correto,
// não é bug). O backend continua a fronteira real (403 se a UI deixar passar).
export const podeConverterLead = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'crm:editar') && temPermissao(perfis, 'associado:criar')

// Assinatura eletrônica (PR1, #604 / PR3): gerar o link de assinatura na
// ficha do lead exige assinatura:criar; revogar (fora de escopo desta PR,
// exportado por paridade com o backend) é crítica/MFA, só gestor/admin.
export const podeVerAssinatura = (perfis?: string[]): boolean => temPermissao(perfis, 'assinatura:ver')
export const podeCriarAssinatura = (perfis?: string[]): boolean => temPermissao(perfis, 'assinatura:criar')
export const podeCancelarAssinatura = (perfis?: string[]): boolean => temPermissao(perfis, 'assinatura:cancelar')

// #321: "Meu Painel" (Carteira/Extrato do parceiro) é uma aba a mais dentro do
// /crm — não uma rota própria — visível só pra quem tem voluntario:ver.
export const podeVerPainelParceiro = (perfis?: string[]): boolean => temPermissao(perfis, 'voluntario:ver')

// podeVerBugdrop — gate de EXIBIÇÃO do widget de feedback (Bugdrop), não uma
// permissão RBAC real (não existe recurso:ação correspondente no backend —
// o widget não expõe nenhum dado/ação sensível, é só um botão flutuante de
// feedback). Lista de perfis fixa por decisão do dono (issue #216): não
// inclui consultor.
const PERFIS_COM_BUGDROP = ['admin', 'gestor', 'operador', 'analista_financeiro', 'analista_risco']

export function podeVerBugdrop(perfis?: string[]): boolean {
  if (!perfis || perfis.length === 0) return false
  return perfis.some((perfil) => PERFIS_COM_BUGDROP.includes(perfil))
}

// Integrações Bancárias (frente bancária) — config de conta bancária por tenant.
// Leitura mascarada (ver/listar); configurar (CRUD de credencial) e testar
// conexão (ver_status) são críticas/MFA no backend. O backend continua a
// fronteira real; aqui só gate de UI.
export const podeVerBanco = (perfis?: string[]): boolean => temPermissao(perfis, 'banco:listar')
export const podeConfigurarBanco = (perfis?: string[]): boolean => temPermissao(perfis, 'banco:configurar')
export const podeTestarConexaoBanco = (perfis?: string[]): boolean => temPermissao(perfis, 'banco:ver_status')

// Cobrança no cartão (Getnet, CARTAO-04). Remover cartão e estornar são ações
// críticas (MFA no backend); configurar a conta também.
export const podeVerCobrancaCartao = (perfis?: string[]): boolean => temPermissao(perfis, 'cobranca_cartao:ver')
export const podeGerenciarCobrancaCartao = (perfis?: string[]): boolean => temPermissao(perfis, 'cobranca_cartao:gerenciar')
export const podeConfigurarCobrancaCartao = (perfis?: string[]): boolean => temPermissao(perfis, 'cobranca_cartao:configurar')
export const podeRemoverCartao = (perfis?: string[]): boolean => temPermissao(perfis, 'cobranca_cartao:remover_cartao')
/** Mandar o título para boleto / voltar o bem para boleto (crítica, MFA). */
export const podeMandarParaBoleto = (perfis?: string[]): boolean => temPermissao(perfis, 'cobranca_cartao:mandar_boleto')
export const podeCobrarNoCartao = (perfis?: string[]): boolean => temPermissao(perfis, 'cobranca_cartao:cobrar')
export const podeEstornarCobrancaCartao = (perfis?: string[]): boolean => temPermissao(perfis, 'cobranca_cartao:estornar')

// White-label (PR1): gate de UI para a tela de Marca (PR2). A LEITURA do
// branding (tema) é universal (todo logado), não passa por aqui — este helper
// gateia só a ESCRITA/gestão da marca.
export const podeGerenciarTenant = (perfis?: string[]): boolean => temPermissao(perfis, 'tenant:gerenciar')

// Integrações de dados (#246): os dois interruptores do preenchimento
// automático. Recurso próprio, não reusa banco:configurar — cada consulta
// custa crédito real, e autorizar gasto é decisão distinta de configurar
// conta bancária ou marca.
export const podeVerIntegracaoDados = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'integracao_dados:ver')
export const podeGerenciarIntegracaoDados = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'integracao_dados:gerenciar')

// Product analytics (#225): leitura agregada de analytics_evento. Recurso
// próprio, não reusa relatorio:ver — relatório é métrica de NEGÓCIO (leads,
// cobrança); isto é uso do PRODUTO (que tela, que etapa, onde desiste). Sem
// gate de MFA: o dado é agregado e o write já rejeita chave-PII em qualquer
// profundidade.
export const podeVerAnalytics = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'analytics:ver')

// Assinatura eletrônica — postura de segurança (PR7): flag
// assinatura.otp_opcional (recurso PRÓPRIO auth.RecIntegracao no backend, ver
// rbac.go). Mais restrito que integracao_dados acima — hoje NENHUM perfil
// além de admin ganha integracao:ver/gerenciar explicitamente na matriz (só
// via wildcard *:*), por ser controle de postura/evidência jurídica, não
// operação de rotina.
export const podeVerIntegracoes = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'integracao:ver')
export const podeGerenciarIntegracoes = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'integracao:gerenciar')

// Recursos e interruptores (Configurações): só admin, via curinga. `gerenciar` é
// ação crítica (MFA no backend).
export const podeVerRecursos = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'recurso:ver')
export const podeGerenciarRecursos = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'recurso:gerenciar')

/**
 * Comissão de parceiros (#232/#233) — três leituras distintas, propositalmente.
 *
 * A tela de Desempenho mostrava os quatro blocos (metas, percentual, dados de
 * pagamento, fila de comissões) atrás de um gate só, `crm:gerenciar`. Duas
 * consequências: o formulário de meta aparecia para quem o backend recusava
 * (metas exigem `voluntario:gerenciar`), e gerar título em Contas a Pagar —
 * passivo financeiro real — ficava ao alcance de quem administra o funil.
 */
export const podeVerComissoes = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'comissao:ver')

/** Decisão comercial: percentual do parceiro. */
export const podeGerenciarComissoes = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'comissao:gerenciar')

/** Ato financeiro: dados de pagamento e geração de título (crítico + MFA). */
export const podePagarComissoes = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'comissao:pagar')

/**
 * Limites de desconto e acréscimo (#889) — ver, configurar e aprovar são três
 * gates, e não um só, porque o maker-checker vive entre eles: quem propõe a
 * regra que mexe em contribuição mensal ou participação não pode liberá-la.
 */
export const podeVerLimitesDescontoAcrescimo = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'desconto_acrescimo:ver')

/** Cria e encerra regra de limite (crítico + MFA). */
export const podeConfigurarLimitesDescontoAcrescimo = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'desconto_acrescimo:configurar')

/** Libera a regra proposta que alcança o rateio (crítico + MFA). */
export const podeAprovarLimitesDescontoAcrescimo = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'desconto_acrescimo:aprovar')

/** Metas de indicação do parceiro — gate PRÓPRIO, é o que o backend exige. */
export const podeGerenciarMetasParceiro = (perfis?: string[]): boolean =>
  temPermissao(perfis, 'voluntario:gerenciar')

